import { Link, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { authStore } from '@/stores/authStore';
import UserMenu from './UserMenu';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Search, Menu, X, Download } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';

// Define BeforeInstallPromptEvent interface
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

// Define enhanced Navigator interface that includes standalone property
interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

const Navbar = observer(() => {
  const user = authStore.user;
  const currentOrg = authStore.currentOrganization;
  const { t } = useTranslation();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  
  // PWA installation states
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  
  // Debug mode - set to true to always show the install button during development
  // You can toggle this to true for testing
  const forceShowInstallButton = import.meta.env.VITE_DEV;

  if (!user) return null;
  
  // Check if path is active
  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };
  
  // Function to get the absolute URL for logo if it's a relative path
  const getLogoUrl = (logoPath: string | undefined) => {
    if (!logoPath) return undefined;
    if (logoPath.startsWith('http')) return logoPath;
    return `${import.meta.env.VITE_API_URL}${logoPath}`;
  };
  
  // Function to truncate organization name 
  const getOrgName = (name?: string) => {
    const defaultName = t('common.yourApp', 'Your App');
    if (!name) return defaultName;
    
    // Always truncate to max 15 chars
    return name.length > 15 ? `${name.substring(0, 15)}...` : name;
  };
  
  // Navigation links with shorter names
  const navigationLinks = [
    { name: t("navigation.dashboard", "Dashboard"), path: "/dashboard" },
    { name: t("navigation.deviceSchedules", "Schedules"), path: "/devices/schedules" },
    { name: t("navigation.deviceMap", "Map"), path: "/devices/map" },
    { name: t("navigation.charts", "Charts"), path: "/devices/charts" },
    { name: t("navigation.deviceManagement", "Devices"), path: "/devices/management" },
    { name: t("navigation.events", "Events"), path: "/events" },
  ];

  // PWA installation event listener
  useEffect(() => {
    // Log initial PWA status for debugging
    console.log('PWA Debug - Is in dev mode:', import.meta.env.DEV);
    console.log('PWA Debug - Is display-mode standalone:', window.matchMedia('(display-mode: standalone)').matches);
    
    // Detect if already installed as PWA
    const isInstalledAsPWA = () => {
      return window.matchMedia('(display-mode: standalone)').matches || 
             (window.navigator as NavigatorWithStandalone).standalone || 
             document.referrer.includes('android-app://');
    };
    
    // Log if already installed
    if (isInstalledAsPWA()) {
      console.log('PWA Debug - App is already installed as PWA');
    }
    
    // Make sure we're handling this browser-only logic
    if (typeof window !== 'undefined') {
      console.log('PWA Debug - Checking if beforeinstallprompt event is supported:', 'onbeforeinstallprompt' in window);
      
      // Force installable in development mode for testing
      if (import.meta.env.DEV && forceShowInstallButton) {
        console.log('PWA Debug - Development mode with forced install button');
        setIsInstallable(true);
      }
    }
    
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('PWA Debug - beforeinstallprompt event fired', e);
      
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      
      // Stash the event so it can be triggered later
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Update UI to notify the user they can install the PWA
      setIsInstallable(true);
      
      console.log('PWA Debug - Install prompt captured and button should be visible');
    };

    // Also check if the app is already in standalone mode (installed)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) {
      console.log('PWA Debug - App is running in standalone mode (already installed)');
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Also listen for appinstalled event
    const handleAppInstalled = () => {
      console.log('PWA Debug - App was installed');
      setIsInstallable(false);
    };
    
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []); // Empty dependency array, we want this to run only once

  // Function to handle PWA installation
  const handleInstallClick = async () => {
    console.log('PWA Debug - Install button clicked, deferredPrompt:', deferredPrompt);
    
    if (!deferredPrompt) {
      console.log('PWA Debug - No deferred prompt available');
      
      // Check PWA requirements and show detailed error message
      let errorMessage = 'Installation failed: No installation prompt available.\n\nThis could happen if:\n- The app is already installed\n- The browser doesn\'t support installation\n- The site doesn\'t meet PWA criteria\n\n';
      
      // Add specific checks
      if (window.matchMedia('(display-mode: standalone)').matches) {
        errorMessage += '- App is already in standalone mode\n';
      }
      
      if (!('serviceWorker' in navigator)) {
        errorMessage += '- Service Worker is not supported\n';
      } else if (!navigator.serviceWorker.controller) {
        errorMessage += '- Service Worker is not controlling the page\n';
      }
      
      if (!('onbeforeinstallprompt' in window)) {
        errorMessage += '- beforeinstallprompt event is not supported\n';
      }
      
      alert(errorMessage);
      return;
    }
    
    try {
      // Show the install prompt
      console.log('PWA Debug - Calling prompt()');
      await deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      console.log('PWA Debug - User choice outcome:', outcome);
      
      // We've used the prompt, and can't use it again, throw it away
      setDeferredPrompt(null);
      
      // Hide the install button
      setIsInstallable(false);
    } catch (error) {
      console.error('PWA Debug - Error during installation:', error);
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && 
          buttonRef.current && 
          !menuRef.current.contains(event.target as Node) && 
          !buttonRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // Handle closing the menu when language is changed or links are clicked
  const handleCloseMenu = () => {
    setMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center">
              {currentOrg?.logo ? (
                <img 
                  src={getLogoUrl(currentOrg.logo)} 
                  alt={currentOrg.name} 
                  className="h-8 w-auto max-w-[120px] object-contain"
                />
              ) : (
                <span className="text-xl font-bold max-w-[120px] truncate">{getOrgName(currentOrg?.name)}</span>
              )}
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:ml-6 md:flex md:space-x-4">
              {navigationLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 text-sm font-medium ${
                    isActive(link.path)
                      ? "text-primary border-b-2 border-primary"
                      : "text-gray-700 hover:text-gray-900 hover:border-b-2 hover:border-gray-300"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Install PWA Button - Desktop */}
            {(isInstallable || forceShowInstallButton) && (
              <div className="hidden md:block">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleInstallClick}
                  className="flex items-center gap-1"
                >
                  <Download className="h-4 w-4" />
                  <span>{t("navigation.installApp", "Install App")}</span>
                </Button>
              </div>
            )}
            
            {/* Desktop - Show on larger screens */}
            <div className="hidden md:flex md:items-center md:space-x-4">
              <LanguageSwitcher />
              <UserMenu />
            </div>
            
            {/* Mobile menu button */}
            <div className="md:hidden relative">
              <button 
                ref={buttonRef}
                type="button" 
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none transition-colors"
                onClick={toggleMenu}
                aria-expanded={menuOpen}
              >
                <span className="sr-only">Toggle menu</span>
                {menuOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
              
              {/* Mobile dropdown menu */}
              {menuOpen && (
                <div 
                  ref={menuRef}
                  className="absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
                >
                  <div className="py-1">
                    {/* Navigation links */}
                    {navigationLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`block px-4 py-2 text-sm ${
                          isActive(link.path)
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                        onClick={handleCloseMenu}
                      >
                        {link.name}
                      </Link>
                    ))}
                    
                    {/* Install PWA Button - Mobile */}
                    {(isInstallable || forceShowInstallButton) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          handleInstallClick();
                          handleCloseMenu();
                        }}
                        className="w-full text-left flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {t("navigation.installApp", "Install App")}
                      </button>
                    )}
                    
                    {/* Mobile divider */}
                    <div className="border-t border-gray-200 my-2" />
                    
                    {/* Language switcher in mobile menu */}
                    <div className="px-4 py-2">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                        {t('common.language', 'Language')}
                      </div>
                      <div className="ml-1">
                        <LanguageSwitcher isMobile={true} onLanguageChange={handleCloseMenu} />
                      </div>
                    </div>
                    
                    {/* User menu in mobile menu */}
                    <div className="px-4 py-2">
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                        {t('common.account', 'Account')}
                      </div>
                      <div className="ml-1 w-full">
                        <UserMenu isMobile={true} onMenuItemClick={handleCloseMenu} />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
});

export default Navbar; 